import {
  IconAlertCircle,
  IconFileDescription,
  IconUpload,
  IconDownload,
  IconCircleCheck,
  IconX,
} from "@tabler/icons-react";
import { clsx } from "clsx";
import { useRef } from "react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { IconPill } from "../../../../components/common/icon-pill";
import { Loader } from "../../../../components/common/loader";
import { DocumentDtoStatusEnum, type DocumentDto, type UserDtoDocumentsInnerTypeEnum } from "../../../../api";
import { documentsService } from "../../../api_client";
import { showErrorToast } from "../../../../utils/toasts";
import { handleAxiosError } from "../../../../common/error_handling";
import { getDocumentDisplayName } from "../../../../utils/onboarding";
import { useDocuments } from "../../hooks/useDocuments";
import { useDocumentUpload } from "../../../onboarding/hooks/useDocumentUpload";

const EXPIRY_WARNING_DAYS = 30;

function formatBytes(bytes?: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ExpiryInfo {
  state: "ok" | "warning" | "expired" | "none";
  daysUntilExpiry: number | null;
  expiresAt: Date | null;
}

function ReuploadButton({
  type,
  onUpload,
  disabled,
}: {
  type: UserDtoDocumentsInnerTypeEnum;
  onUpload: (type: UserDtoDocumentsInnerTypeEnum, file: File) => Promise<void>;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        disabled={disabled}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          await onUpload(type, file);
        }}
      />
      <Button
        variant="primary"
        size="sm"
        leftIcon={<IconUpload size={15} />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
      >
        {disabled ? "Uploading..." : "Re-upload"}
      </Button>
    </>
  );
}

function UploadProgressBar({ progress }: { progress: number }) {
  const percent = Math.round(Math.max(0, Math.min(1, progress)) * 100);

  return (
    <div className="ml-14 mr-2 mt-2 max-md:ml-0">
      <Text variant="bodyXs" tone="body">
        Uploading {percent}%
      </Text>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-uiSecondary">
        <div
          className="h-full bg-primary transition-[width]"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function expiryInfo(doc: DocumentDto): ExpiryInfo {
  if (!doc.expiresAt) {
    return { state: "none", daysUntilExpiry: null, expiresAt: null };
  }
  const expiresAt = new Date(doc.expiresAt);
  const ms = expiresAt.getTime() - Date.now();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (ms <= 0) return { state: "expired", daysUntilExpiry: days, expiresAt };
  if (days <= EXPIRY_WARNING_DAYS) return { state: "warning", daysUntilExpiry: days, expiresAt };
  return { state: "ok", daysUntilExpiry: days, expiresAt };
}

async function downloadDoc(doc: DocumentDto) {
  try {
    const resp = await documentsService.getDocumentDownloadUrl(doc.id);
    const url = (resp.data as { url?: string }).url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  } catch (err) {
    showErrorToast(handleAxiosError(err).message);
  }
}

export function DocumentsSection({
  showExpiryWarning = false,
}: {
  showExpiryWarning?: boolean;
}) {
  const { data, isLoading, mutate } = useDocuments({ page: 1, pageSize: 100 });
  const {
    uploading,
    uploadProgress,
    uploadDocument,
  } = useDocumentUpload({
    onUploadSuccess: () => {
      void mutate();
    },
  });
  const docs = data?.data ?? [];
  const hasRejected = docs.some(
    (d) => d.status === DocumentDtoStatusEnum.Rejected,
  );

  // Latest approved doc per type. We don't replace older approved docs on
  // re-upload (they stay for audit), so the "active" copy is the most
  // recently approved one. Pending replacements are shown separately below.
  const latestApprovedByType = new Map<string, DocumentDto>();
  for (const d of docs) {
    if (d.status !== DocumentDtoStatusEnum.Approved || !d.type) continue;
    const existing = latestApprovedByType.get(d.type);
    if (!existing || new Date(d.createdAt) > new Date(existing.createdAt)) {
      latestApprovedByType.set(d.type, d);
    }
  }
  const activeApprovedIds = new Set(
    Array.from(latestApprovedByType.values()).map((d) => d.id),
  );

  // Pending replacement per type — when present, the active approved doc
  // shouldn't get a "Re-upload" prompt because one is already in flight.
  const pendingByType = new Map<string, DocumentDto>();
  for (const d of docs) {
    if (d.status !== DocumentDtoStatusEnum.Pending || !d.type) continue;
    const existing = pendingByType.get(d.type);
    if (!existing || new Date(d.createdAt) > new Date(existing.createdAt)) {
      pendingByType.set(d.type, d);
    }
  }

  // Group all docs by type so we can render Tax Card / Commercial Reg /etc.
  // as labeled sections with both active and in-review versions visible.
  const groupedByType = new Map<string, DocumentDto[]>();
  for (const d of docs) {
    const key = d.type ?? "__OTHER__";
    if (!groupedByType.has(key)) groupedByType.set(key, []);
    groupedByType.get(key)!.push(d);
  }
  // Within each group: pending → active approved → other approved (newest
  // first) → rejected. Keeps the eye-line on what's actionable.
  function rankWithinGroup(d: DocumentDto): number {
    if (d.status === DocumentDtoStatusEnum.Pending) return 0;
    if (d.status === DocumentDtoStatusEnum.Approved && activeApprovedIds.has(d.id)) return 1;
    if (d.status === DocumentDtoStatusEnum.Approved) return 2;
    return 3;
  }
  for (const list of groupedByType.values()) {
    list.sort((a, b) => {
      const r = rankWithinGroup(a) - rankWithinGroup(b);
      if (r !== 0) return r;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  // Show the global header banner only if the showExpiryWarning prop is set
  // and at least one ACTIVE approved doc is in the warning window.
  const anyActiveExpiringSoon = Array.from(latestApprovedByType.values()).some(
    (d) => expiryInfo(d).state === "warning" || expiryInfo(d).state === "expired",
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite overflow-hidden">
        {showExpiryWarning && !hasRejected && anyActiveExpiringSoon && (
          <div className="flex items-center gap-4 mx-6 mt-5 rounded-2xl border border-accentAmber bg-accentYellowTint px-5 py-4 max-md:mx-4 max-md:px-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accentAmberDark">
              <IconAlertCircle size={20} className="text-bgWhite" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Text variant="bodyBold" tone="inherit" className="!text-accentAmberDark">
                One or more of your documents is expiring soon.
              </Text>
              <Text variant="bodySm" tone="inherit" className="!text-accentAmberDark">
                Re-upload the affected document so admin can re-verify before it expires.
              </Text>
            </div>
          </div>
        )}

        {hasRejected && (
          <div className="flex items-center gap-4 mx-6 mt-5 rounded-2xl bg-accentYellowTint px-5 py-4 max-md:mx-4 max-md:px-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accentAmberDark">
              <IconX size={18} className="text-bgWhite" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Text variant="bodyBold" tone="inherit" className="!text-accentError">
                One or more of your documents could not be verified.
              </Text>
              <Text variant="bodySm" tone="inherit" className="!text-accentError">
                Your document was not approved. Please resubmit to continue.
              </Text>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1 px-6 pt-5 pb-3 max-md:px-4">
          <Heading variant="h3" tone="dark">
            Verification Documents
          </Heading>
          <Text variant="body" tone="dark">
            Upload and manage your company verification documents
          </Text>
        </div>

        <div className="flex flex-col gap-5 px-6 pt-0 pb-4 max-md:px-4">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader />
            </div>
          ) : docs.length === 0 ? (
            <Text variant="body" tone="muted" className="py-4 text-center">
              No documents uploaded yet.
            </Text>
          ) : (
            Array.from(groupedByType.entries()).map(([typeKey, group]) => {
              const groupLabel =
                typeKey === "__OTHER__"
                  ? "Other"
                  : getDocumentDisplayName(typeKey as UserDtoDocumentsInnerTypeEnum);
              const hasPendingForType =
                typeKey !== "__OTHER__" && pendingByType.has(typeKey);

              return (
                <div key={typeKey} className="flex flex-col gap-2">
                  <Text variant="capsBig" tone="muted">
                    {groupLabel}
                  </Text>
                  {group.map((doc) => {
                    const status = doc.status;
                    const isRejected = status === DocumentDtoStatusEnum.Rejected;
                    const isApproved = status === DocumentDtoStatusEnum.Approved;
                    const isPending = status === DocumentDtoStatusEnum.Pending;
                    const isActiveApproved = isApproved && activeApprovedIds.has(doc.id);
                    const isReplacedApproved = isApproved && !isActiveApproved;
                    const docType = doc.type as UserDtoDocumentsInnerTypeEnum | undefined;
                    const isUploading = docType ? uploading[docType] : false;
                    const progress = docType ? uploadProgress[docType] : 0;

                    const expiry = expiryInfo(doc);
                    const showExpiryBadge =
                      isActiveApproved &&
                      (expiry.state === "warning" || expiry.state === "expired");

                    // Per-doc re-upload trigger: rejected docs always; active
                    // approved docs only if expiring within 30 days AND no
                    // pending replacement is already in flight for this type.
                    const offerReupload =
                      isRejected || (showExpiryBadge && !hasPendingForType);

                    return (
                      <div key={doc.id} className="flex flex-col gap-0.5">
                        <div
                          className={clsx(
                            "flex items-center gap-4 rounded-2xl border px-4 py-4 max-md:gap-3 max-md:px-3",
                            isRejected
                              ? "border-accentError"
                              : showExpiryBadge
                                ? "border-accentAmber"
                                : "border-borderDefault",
                            isReplacedApproved && "opacity-70",
                          )}
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bgLight border border-borderDefault max-md:hidden">
                            <IconFileDescription size={20} className="text-textMuted" />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <Text variant="bodyBold" tone="dark" className="max-md:break-all">
                                {doc.fileName}
                              </Text>
                              {isActiveApproved && (
                                <Chip variant="approved" icon={<IconCircleCheck size={14} />}>
                                  Active
                                </Chip>
                              )}
                              {isReplacedApproved && (
                                <Chip variant="inReview">Previous version</Chip>
                              )}
                              {isPending && (
                                <Chip variant="inReview">
                                  {latestApprovedByType.has(typeKey)
                                    ? "In review (replacement)"
                                    : "In review"}
                                </Chip>
                              )}
                              {isRejected && (
                                <Chip variant="rejected" icon={<IconX size={12} />}>
                                  Rejected
                                </Chip>
                              )}
                              {showExpiryBadge && expiry.state === "expired" && (
                                <Chip variant="rejected" icon={<IconAlertCircle size={12} />}>
                                  Expired
                                </Chip>
                              )}
                              {showExpiryBadge && expiry.state === "warning" && (
                                <Chip variant="inReview" icon={<IconAlertCircle size={12} />}>
                                  Expires in {expiry.daysUntilExpiry}{" "}
                                  {expiry.daysUntilExpiry === 1 ? "day" : "days"}
                                </Chip>
                              )}
                            </div>
                            <Text variant="bodySm" tone="muted">
                              {formatBytes(doc.fileSize)}
                              {expiry.expiresAt &&
                                ` · Expires ${expiry.expiresAt.toLocaleDateString()}`}
                            </Text>
                          </div>
                          {offerReupload ? (
                            docType ? (
                              <ReuploadButton
                                type={docType}
                                onUpload={uploadDocument}
                                disabled={isUploading}
                              />
                            ) : null
                          ) : (
                            <IconPill
                              as="button"
                              size={36}
                              tone="ghost"
                              shape="square"
                              type="button"
                              aria-label={`Download ${doc.fileName}`}
                              onClick={() => downloadDoc(doc)}
                            >
                              <IconDownload size={18} />
                            </IconPill>
                          )}
                        </div>
                        {isRejected && doc.rejectionReason && (
                          <Text
                            variant="bodySmBold"
                            tone="inherit"
                            className="pl-5 !text-accentError"
                          >
                            Reason: {doc.rejectionReason}
                          </Text>
                        )}
                        {isUploading && docType ? (
                          <UploadProgressBar progress={progress} />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
